import React, { Component } from 'react';
import styled from 'styled-components';
import isEqual from 'lodash.isequal';
import Honeybadger from '@honeybadger-io/js';
import { connect } from 'react-redux';
import { themes } from 'kb-shared';
import { get as _get } from 'lodash';

import {
  utils,
  Appointment,
  ProfileImageError,
  PatientToDo,
  isHardcodedToDoCard,
  isPatientToDo,
  ReduxState,
  ToDoCard,
  HARDCODED_TODO_MAP,
  graphql
} from 'kb-shared';
import { TabNavigator } from 'kb-web-shared';
import ProfilePhoto from './ProfilePhoto';
import IntakeForm from './IntakeForm';
import UploadInsuranceCard from './UploadInsuranceCard';
import UploadID from './uploadID';
import ToDoRow from './ToDoRow';
import ToDoCount from './TodoCount';
import InsuranceCardLoader from '../../PhotoUpload/InsuranceCard/util/InsuranceCardLoader';
import IDLoader from '../../PhotoUpload/ID/util/IDLoader';
import { PORTAL_FEATURE_DISCOVERY_CATEGORY } from './constants';
import { MARK_LEARN_ABOUT_KB360_COMPLETE } from '../graphql/mutations';

const {
  isAssessment,
  getInsuranceCardWasSkipped,
  isGYNAppointment,
  isVirtualConsultation,
  isPulseUltrasoundBloodwork
} = utils;

const Container = styled.div`
  margin-bottom: 30px;
  width: 100%;
`;

const CardContainer = styled.div`
  flex: 1;
`;
type Props = {
  appointments: Appointment[];
  profileImageError: ProfileImageError;
  profileImage?: string;
  apiTodos: PatientToDo;
  submittedInsuranceCard: boolean;
  intakeFormCompletionPercent: number;
  gender: string;
  submittedID: boolean;
  showUploadID: boolean;
};

type State = {
  index: number;
  cards: ToDoCard[];
  isGyn: boolean;
  isMale: boolean;
};

class ToDoIngestor extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { cards, index, isGyn, isMale } = this._getCardState();

    this.state = {
      index,
      cards,
      isGyn,
      isMale
    };
  }

  componentDidMount() {
    window.addEventListener('storage', this._handleStorageChange);
  }

  componentDidUpdate(prevProps: Props) {
    const {
      profileImage,
      profileImageError,
      apiTodos,
      submittedInsuranceCard,
      submittedID,
      showUploadID
    } = this.props;
    const {
      profileImage: prevProfileImage,
      profileImageError: prevProfileImageError,
      apiTodos: prevApiTodos,
      submittedInsuranceCard: prevSubmittedInsuranceCard,
      submittedID: prevSubmittedID,
      showUploadID: prevShowUploadID
    } = prevProps;
    if (
      profileImage === prevProfileImage &&
      profileImageError === prevProfileImageError &&
      submittedInsuranceCard === prevSubmittedInsuranceCard &&
      submittedID === prevSubmittedID &&
      showUploadID === prevShowUploadID &&
      isEqual(apiTodos, prevApiTodos)
    )
      return;

    const { cards, index, isGyn, isMale } = this._getCardState();

    this.setState({ index, cards, isGyn, isMale });
  }

  componentWillUnmount() {
    window.removeEventListener('storage', this._handleStorageChange);
  }

  _handleStorageChange = () => {
    const { index, cards, isGyn, isMale } = this._getCardState();
    this.setState({ cards, index, isGyn, isMale });
  };

  _getCardState = () => {
    const {
      appointments,
      profileImage,
      profileImageError,
      apiTodos,
      submittedInsuranceCard,
      intakeFormCompletionPercent = 0,
      gender,
      submittedID,
      showUploadID
    } = this.props;
    const index = (this.state && this.state.index) || 0;
    const hasAssessment =
      appointments.filter(app => isAssessment(app, process.env.REACT_APP_ENV)).length > 0;
    const hasGyn =
      appointments.filter(app =>
        isGYNAppointment(parseInt(app.appointmentType.category.id), process.env.REACT_APP_ENV)
      ).length > 0;
    const hasVirtualConsultation =
      appointments.filter(app => isVirtualConsultation(app, process.env.REACT_APP_ENV)).length > 0;
    const isMale = gender === 'male';
    const isPulseAssessement =
      appointments.filter(app => isPulseUltrasoundBloodwork(app, process.env.REACT_APP_ENV))
        .length > 0;
    const showIntake =
      (intakeFormCompletionPercent && intakeFormCompletionPercent < 100) ||
      ((hasAssessment || hasGyn || hasVirtualConsultation) && !intakeFormCompletionPercent) ||
      (isMale && !intakeFormCompletionPercent) ||
      (isPulseAssessement && intakeFormCompletionPercent < 100);

    const insuranceCardSkipped = getInsuranceCardWasSkipped();

    let nextCards: ToDoCard[] = [];
    if (showIntake) nextCards.push(HARDCODED_TODO_MAP.intake);
    if (!profileImage || profileImageError) nextCards.push(HARDCODED_TODO_MAP.profileImage);
    if (!submittedInsuranceCard && !insuranceCardSkipped) {
      nextCards.push(HARDCODED_TODO_MAP.uploadInsurance);
    }
    if (!submittedID && showUploadID) nextCards.push(HARDCODED_TODO_MAP.uploadID);
    nextCards = nextCards
      .concat(apiTodos)
      .filter((todo, index, self) => index === self.findIndex(sTodo => sTodo.title === todo.title));
    if (intakeFormCompletionPercent && intakeFormCompletionPercent === 100)
      nextCards = nextCards.filter(todo => todo.title !== 'Complete Intake Form');

    const nextIndex = nextCards[index] ? index : 0;

    return {
      cards: nextCards,
      index: nextIndex,
      isGyn: hasGyn,
      isMale: isMale
    };
  };

  _selectCard = (index: number) => {
    const currentCard = this.state.cards[index];
    if (!currentCard) {
      const errorMessage = `Index: ${index} not found in to do items`;
      if (process.env.REACT_APP_ENV === 'staging') {
        console.error(errorMessage);
      } else {
        Honeybadger.notify(errorMessage, 'TodoIngestorError');
      }
    }

    this.setState({ index });
  };

  _renderCard(card: ToDoCard, isGyn: boolean, isMale: boolean) {
    if (isHardcodedToDoCard(card)) {
      const { type, explainer } = card;
      switch (type) {
        case 'intake':
          return <IntakeForm isGyn={isGyn} explainer={explainer} isMale={isMale} />;
        case 'profileImage':
          return <ProfilePhoto explainer={explainer} />;
        case 'uploadInsurance':
          return <UploadInsuranceCard explainer={explainer} />;
        case 'uploadID':
          return <UploadID explainer={explainer} />;
        default:
          break;
      }
    } else if (isPatientToDo(card)) {
      const { title, description, linkUrl, buttonText } = card;
      const headingText = _get(card, 'headingText', title);

      const onClick = async () => {
        if (!linkUrl) return;

        const category = _get(card, 'category');

        if (category === PORTAL_FEATURE_DISCOVERY_CATEGORY) {
          graphql.client.mutate({ mutation: MARK_LEARN_ABOUT_KB360_COMPLETE });
        }

        window.location.href = linkUrl;
      };

      return (
        <ToDoRow
          title={title}
          description={description}
          buttonText={buttonText}
          headingText={headingText}
          onClick={linkUrl ? onClick : undefined}
          mobile
        />
      );
    }
  }

  renderToDoCount() {
    const { cards } = this.state;
    if (cards.length > 0) {
      return <ToDoCount count={cards.length} />;
    }
    return null;
  }

  render() {
    const { cards, index, isGyn, isMale } = this.state;

    return (
      <Container>
        {this.renderToDoCount()}
        <TabNavigator
          items={cards}
          currentIndex={index}
          getSectionTitle={(index: number, item: ToDoCard) => item.title}
          getUniqueKey={(index: number, item: ToDoCard) => `card-tab-${item.title}`}
          renderContent={(index: number, item: ToDoCard) => {
            return <CardContainer>{this._renderCard(item, isGyn, isMale)}</CardContainer>;
          }}
          onSelect={this._selectCard}
          theme={{
            tab: {
              selectedBG: themes.colors.yellow,
              deselectedBG: themes.colors.yellowLight,
              selectedTitle: themes.colors.night,
              deselectedTitle: themes.colors.nightLight,
              fullWidthTabs: true
            },
            content: {
              selectedBG: themes.colors.yellow,
              deselectedBG: themes.colors.yellowLight
            }
          }}
        />
        <InsuranceCardLoader />
        <IDLoader />
      </Container>
    );
  }
}

export default connect(({ patient: { patient, profileImageError } }: ReduxState) => {
  return {
    profileImage: patient.profileImage,
    profileImageError: profileImageError,
    submittedInsuranceCard: patient.submittedInsuranceCard,
    gender: patient.gender,
    submittedID: patient.submittedID
  };
})(
  // @ts-ignore
  ToDoIngestor
);
